import { useEffect, useCallback, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useAuthStore, useThemeStore, api } from "@/store/useStore";

// PWA Components
import { OfflineBanner, UpdateBanner } from "@/components/pwa/NetworkStatus";
import { useServiceWorker } from "@/lib/pwa-hooks";

// Security - Idle Timeout & Device Registration
import { useIdleTimeout, markSessionStart, clearSessionMarkers } from "@/lib/idle-timeout";
import { isDeviceRegistered, syncDeviceRegistration, getLoginRedirectUrl, lookupDeviceByFingerprint, restoreDeviceByFingerprint } from "@/lib/device-registration";
import IdleWarningDialog from "@/components/IdleWarningDialog";

// Public Pages (eagerly loaded - login flow)
import LandingPage from "@/pages/LandingPage";
import RegisterPage from "@/pages/RegisterPage";
import LoginPage from "@/pages/LoginPage";
import PinLoginPage from "@/pages/PinLoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// eCommerce storefront (public)
import StorefrontLayout from "@/storefront/StorefrontLayout";
const StoreHome = lazy(() => import("@/storefront/StoreHome"));
const StoreCategory = lazy(() => import("@/storefront/StoreCategory"));
const StoreProduct = lazy(() => import("@/storefront/StoreProduct"));
const StoreSearch = lazy(() => import("@/storefront/StoreSearch"));
const StoreAccount = lazy(() => import("@/storefront/StoreAccount"));
const StoreOrders = lazy(() => import("@/storefront/StoreOrders"));
const StoreCheckout = lazy(() => import("@/storefront/StoreCheckout"));
const StoreConfirmation = lazy(() => import("@/storefront/StoreConfirmation"));

// POS (eagerly loaded - critical path)
import POSPage from "@/pages/POSPage";

// Admin Pages (lazy loaded - not on critical path)
import AdminLayout from "@/pages/admin/AdminLayout";
const DashboardPage = lazy(() => import("@/pages/admin/DashboardPage"));
const PropertiesPage = lazy(() => import("@/pages/admin/PropertiesPage"));
const OutletsPage = lazy(() => import("@/pages/admin/OutletsPage"));
const StoresPage = lazy(() => import("@/pages/admin/StoresPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const UserRolesPage = lazy(() => import("@/pages/admin/UserRolesPage"));
const ProductsPage = lazy(() => import("@/pages/admin/ProductsPage"));
const CategoriesPage = lazy(() => import("@/pages/admin/CategoriesPage"));
const InventoryPage = lazy(() => import("@/pages/admin/InventoryPage"));
const SuppliersPage = lazy(() => import("@/pages/admin/SuppliersPage"));
const RequisitionsPage = lazy(() => import("@/pages/admin/RequisitionsPage"));
const GRNPage = lazy(() => import("@/pages/admin/GRNPage"));
const PurchaseOrdersPage = lazy(() => import("@/pages/admin/PurchaseOrdersPage"));
const OrdersPage = lazy(() => import("@/pages/admin/OrdersPage"));
const ReportsPage = lazy(() => import("@/pages/admin/ReportsPage"));
const EODReportsPage = lazy(() => import("@/pages/admin/EODReportsPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const CustomersPage = lazy(() => import("@/pages/admin/CustomersPage"));
const TablesPage = lazy(() => import("@/pages/admin/TablesPage"));
const KitchenPage = lazy(() => import("@/pages/admin/KitchenPage"));
const TaxesPage = lazy(() => import("@/pages/admin/TaxesPage"));
const DiscountsPage = lazy(() => import("@/pages/admin/DiscountsPage"));
const ShiftsPage = lazy(() => import("@/pages/admin/ShiftsPage"));
const OnlineOrdersPage = lazy(() => import("@/pages/admin/OnlineOrdersPage"));
const OnlineStorePage = lazy(() => import("@/pages/admin/OnlineStorePage"));
const PaymentsPage = lazy(() => import("@/pages/admin/PaymentsPage"));
const StockTakesPage = lazy(() => import("@/pages/admin/StockTakesPage"));
const StockValuationPage = lazy(() => import("@/pages/admin/StockValuationPage"));
const AccountingPeriodsPage = lazy(() => import("@/pages/admin/AccountingPeriodsPage"));
const DevicesPage = lazy(() => import("@/pages/admin/DevicesPage"));
const CostCentresPage = lazy(() => import("@/pages/admin/CostCentresPage"));
const StoreIssuesPage = lazy(() => import("@/pages/admin/StoreIssuesPage"));
const ItemReductionsPage = lazy(() => import("@/pages/admin/ItemReductionsPage"));
const OutletPerformancePage = lazy(() => import("@/pages/admin/OutletPerformancePage"));
const SetupWizardPage = lazy(() => import("@/pages/admin/SetupWizardPage"));
const TenantSubscriptionPage = lazy(() => import("@/pages/admin/TenantSubscriptionPage"));
const EtimsPage = lazy(() => import("@/pages/admin/EtimsPage"));
const FiscalReportsPage = lazy(() => import("@/pages/admin/FiscalReportsPage"));
const OptionGroupsPage = lazy(() => import("@/pages/admin/OptionGroupsPage"));
const PrinterTestPage = lazy(() => import("@/pages/PrinterTestPage"));

// Super Admin Pages (lazy loaded)
const SuperAdminLayout = lazy(() => import("@/pages/superadmin/SuperAdminLayout"));
const SuperAdminDashboard = lazy(() => import("@/pages/superadmin/DashboardPage"));
const RegistrationsPage = lazy(() => import("@/pages/superadmin/RegistrationsPage"));
const ContactLeadsPage = lazy(() => import("@/pages/superadmin/ContactLeadsPage"));
const TenantsPage = lazy(() => import("@/pages/superadmin/TenantsPage"));
const SuperAdminSettingsPage = lazy(() => import("@/pages/superadmin/SettingsPage"));
const ErrorLogsPage = lazy(() => import("@/pages/superadmin/ErrorLogsPage"));
const DocumentationPage = lazy(() => import("@/pages/superadmin/DocumentationPage"));
const HelpCenterPage = lazy(() => import("@/pages/superadmin/HelpCenterPage"));
const PlatformSettingsPage = lazy(() => import("@/pages/superadmin/PlatformSettingsPage"));
const SubscriptionPlansPage = lazy(() => import("@/pages/superadmin/SubscriptionPlansPage"));
const MobileAppPage = lazy(() => import("@/pages/superadmin/MobileAppPage"));
const CodeHealthPage = lazy(() => import("@/pages/superadmin/CodeHealthPage"));

// Public Pages (lazy loaded)
const HelpCenterPublic = lazy(() => import("@/pages/HelpCenterPublic"));
const QRMenuPage = lazy(() => import("@/pages/admin/QRMenuPage"));
const PublicMenuPage = lazy(() => import("@/pages/PublicMenuPage"));
const OrderTrackerPage = lazy(() => import("@/pages/OrderTrackerPage"));
const OrderHistoryPage = lazy(() => import("@/pages/OrderHistoryPage"));

// Protected Route Component
const ProtectedRoute = ({ children, requiredPermissions = [] }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermissions.length > 0 && user?.role) {
    const userPermissions = user.role.permissions || [];
    
    // Check if user has any of the required permissions
    const hasPermission = requiredPermissions.some((requiredPerm) => {
      // Check for exact match
      if (userPermissions.includes(requiredPerm)) return true;
      // Check for "all" wildcard
      if (userPermissions.includes("all") || userPermissions.includes("*")) return true;
      // Check for category wildcard (e.g., "pos.*" grants access to "pos")
      if (userPermissions.includes(`${requiredPerm}.*`)) return true;
      // Check if any permission starts with the required permission prefix
      // e.g., if required is "pos", match "pos.create_order", "pos.void_order", etc.
      return userPermissions.some((p) => p.startsWith(`${requiredPerm}.`));
    });
    
    if (!hasPermission) {
      return <Navigate to="/pos" replace />;
    }
  }

  return children;
};

// POS Route Component - Requires device registration AND authentication with permission check
const POSRoute = ({ children, requiredPermissions = [] }) => {
  const { isAuthenticated, user } = useAuthStore();
  const [deviceStatus, setDeviceStatus] = useState({ checking: true, registered: false, active: false });
  const [recognizedDevice, setRecognizedDevice] = useState(null);
  const [restoring, setRestoring] = useState(false);

  // Check device registration on mount (regardless of auth status)
  useEffect(() => {
    const checkDevice = async () => {
      const result = await syncDeviceRegistration();
      
      // If not registered, try fingerprint lookup
      if (!result.registered) {
        const fingerprintResult = await lookupDeviceByFingerprint();
        if (fingerprintResult.found && fingerprintResult.active) {
          setRecognizedDevice(fingerprintResult);
        }
      }
      
      setDeviceStatus({
        checking: false,
        registered: result.registered === true,
        active: result.active === true,
        tenantName: result.tenant_name,
        message: result.message
      });
    };
    checkDevice();
  }, []);

  // Handle device restore
  const handleRestoreDevice = async () => {
    if (!recognizedDevice?.device_id) return;
    
    setRestoring(true);
    try {
      const result = await restoreDeviceByFingerprint(recognizedDevice.device_id);
      if (result.success) {
        // Refresh device status
        setDeviceStatus({
          checking: false,
          registered: true,
          active: true,
          tenantName: result.tenant_name,
          message: "Device restored"
        });
        setRecognizedDevice(null);
        toast.success(`Welcome back! Device "${result.device_name}" restored.`);
      } else {
        toast.error(result.message || "Failed to restore device");
      }
    } catch (error) {
      toast.error("Failed to restore device");
    } finally {
      setRestoring(false);
    }
  };

  // Still checking device status
  if (deviceStatus.checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-3 text-muted-foreground">Verifying device...</p>
        </div>
      </div>
    );
  }

  // Device not registered but recognized by fingerprint - show confirmation
  if (!deviceStatus.registered && recognizedDevice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Device Recognized</h1>
          <p className="text-muted-foreground mb-2">
            This device was previously registered as:
          </p>
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <p className="font-semibold text-lg">{recognizedDevice.device_name}</p>
            <p className="text-sm text-muted-foreground">{recognizedDevice.tenant_name}</p>
            <p className="text-xs text-muted-foreground mt-1">ID: {recognizedDevice.device_id}</p>
          </div>
          <div className="space-y-3">
            <button 
              onClick={handleRestoreDevice}
              disabled={restoring}
              className="inline-flex items-center justify-center w-full px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 font-medium"
            >
              {restoring ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Restoring...
                </>
              ) : (
                <>Continue as {recognizedDevice.device_name}</>
              )}
            </button>
            <button 
              onClick={() => setRecognizedDevice(null)}
              className="inline-flex items-center justify-center w-full px-4 py-2 border border-input bg-background hover:bg-accent rounded-md text-sm"
            >
              Not this device? Register as new
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Device not registered - show error with registration option
  if (!deviceStatus.registered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Device Not Registered</h1>
          <p className="text-muted-foreground mb-6">
            This device is not registered for POS access. Register it with a valid administrator PIN to continue.
          </p>
          <div className="space-y-3">
            <Link
              to="/pin-login?register-device=1"
              className="inline-flex items-center justify-center w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Register Device
            </Link>
            <a 
              href="/login" 
              className="inline-flex items-center justify-center w-full px-4 py-2 border border-input bg-background hover:bg-accent rounded-md"
            >
              Back Office Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Device inactive
  if (!deviceStatus.active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-4">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Device Deactivated</h1>
          <p className="text-muted-foreground mb-6">
            This device has been deactivated by an administrator. Please contact your manager to reactivate it.
          </p>
          <a 
            href="/login" 
            className="inline-flex items-center justify-center w-full px-4 py-2 border border-input bg-background hover:bg-accent rounded-md"
          >
            Back Office Login
          </a>
        </div>
      </div>
    );
  }

  // Device is registered and active - now check authentication
  if (!isAuthenticated) {
    // Redirect registered devices to PIN login
    return <Navigate to="/pin-login" replace />;
  }

  // Check permissions (similar to ProtectedRoute)
  if (requiredPermissions.length > 0 && user?.role) {
    const userPermissions = user.role.permissions || [];
    
    const hasPermission = requiredPermissions.some((requiredPerm) => {
      if (userPermissions.includes(requiredPerm)) return true;
      if (userPermissions.includes("all") || userPermissions.includes("*")) return true;
      if (userPermissions.includes(`${requiredPerm}.*`)) return true;
      return userPermissions.some((p) => p.startsWith(`${requiredPerm}.`));
    });
    
    if (!hasPermission) {
      return <Navigate to="/admin" replace />;
    }
  }

  return children;
};

// Super Admin Route Component
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_super_admin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Idle timeout wrapper - must be inside BrowserRouter
function IdleTimeoutWrapper({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuthStore();
  
  // Public routes that don't need idle timeout
  const publicRoutes = ['/', '/login', '/register', '/pin-login', '/help', '/forgot-password', '/reset-password'];
  const isPublicRoute = publicRoutes.some(route => location.pathname === route || location.pathname.startsWith('/help') || location.pathname.startsWith('/reset-password') || location.pathname.startsWith('/menu'));
  
  // Handle idle timeout logout
  const handleIdleLogout = useCallback(() => {
    if (isAuthenticated && !isPublicRoute) {
      clearSessionMarkers();
      logout();
      
      // Check if device is registered - if so, redirect to PIN login
      const { registered, active } = isDeviceRegistered();
      if (registered && active) {
        toast.info('Session expired. Please enter your PIN to continue.', { duration: 5000 });
        navigate('/pin-login', { replace: true });
      } else {
        toast.info('You have been logged out due to inactivity', { duration: 5000 });
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, isPublicRoute, logout, navigate]);
  
  // Use idle timeout hook - enabled when authenticated and not on public route
  const { showWarning, remainingTime, extendSession } = useIdleTimeout(
    handleIdleLogout,
    isAuthenticated && !isPublicRoute
  );
  
  return (
    <>
      {children}
      <IdleWarningDialog
        open={showWarning}
        remainingTime={remainingTime}
        onExtend={extendSession}
        onLogout={handleIdleLogout}
      />
    </>
  );
}

function App() {
  const { theme, setTheme } = useThemeStore();
  const { isAuthenticated, user } = useAuthStore();
  
  // Initialize PWA service worker
  useServiceWorker();

  // Remove Emergent badge to prevent UI interference
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.remove();
      }
      // Also try to find by content
      document.querySelectorAll('a[href*="emergent"]').forEach(el => {
        if (el.textContent?.includes('Made with Emergent') || el.style?.position === 'fixed') {
          el.remove();
        }
      });
    };
    
    // Remove immediately and after a delay (in case it's injected later)
    removeBadge();
    const timer1 = setTimeout(removeBadge, 500);
    const timer2 = setTimeout(removeBadge, 1500);
    const timer3 = setTimeout(removeBadge, 3000);
    
    // Also set up a mutation observer to catch dynamically added badges
    const observer = new MutationObserver(() => {
      removeBadge();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Apply theme on mount
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    // Seed database on first load
    const seedDb = async () => {
      try {
        await api.post("/seed");
      } catch (e) {
        // Already seeded or error
      }
    };
    seedDb();
  }, [theme]);

  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <IdleTimeoutWrapper>
        <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/pin-login" element={<PinLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Public eCommerce Storefront (per tenant) */}
          <Route path="/store/:code" element={<StorefrontLayout />}>
            <Route index element={<StoreHome />} />
            <Route path="c/:categoryId" element={<StoreCategory />} />
            <Route path="p/:productId" element={<StoreProduct />} />
            <Route path="search" element={<StoreSearch />} />
            <Route path="account" element={<StoreAccount />} />
            <Route path="account/orders" element={<StoreOrders />} />
            <Route path="checkout" element={<StoreCheckout />} />
            <Route path="order/:orderNo" element={<StoreConfirmation />} />
          </Route>

          {/* POS Route - Uses POSRoute which handles both auth and device registration */}
          <Route
            path="/pos"
            element={
              <POSRoute requiredPermissions={["pos", "all"]}>
                <POSPage />
              </POSRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/superadmin"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout />
              </SuperAdminRoute>
            }
          >
            <Route index element={<SuperAdminDashboard />} />
            <Route path="registrations" element={<RegistrationsPage />} />
            <Route path="contact-leads" element={<ContactLeadsPage />} />
            <Route path="tenants" element={<TenantsPage />} />
            <Route path="subscriptions" element={<TenantsPage />} />
            <Route path="plans" element={<SubscriptionPlansPage />} />
            <Route path="error-logs" element={<ErrorLogsPage />} />
            <Route path="documentation" element={<DocumentationPage />} />
            <Route path="help-center" element={<HelpCenterPage />} />
            <Route path="platform-settings" element={<PlatformSettingsPage />} />
            <Route path="mobile-app" element={<MobileAppPage />} />
            <Route path="code-health" element={<CodeHealthPage />} />
            <Route path="settings" element={<SuperAdminSettingsPage />} />
          </Route>

          {/* Public Help Center */}
          <Route path="/help" element={<HelpCenterPublic />} />

          {/* Public QR Menu */}
          <Route path="/menu/:slug" element={<PublicMenuPage />} />
          <Route path="/menu/:slug/track/:orderId" element={<OrderTrackerPage />} />
          <Route path="/menu/:slug/orders" element={<OrderHistoryPage />} />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredPermissions={["all", "admin", "report", "inv", "prod"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="properties" element={<PropertiesPage />} />
            <Route path="outlets" element={<OutletsPage />} />
            <Route path="stores" element={<StoresPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="user-roles" element={<UserRolesPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="option-groups" element={<OptionGroupsPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="suppliers" element={<SuppliersPage />} />
            <Route path="requisitions" element={<RequisitionsPage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="grn" element={<GRNPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="online-orders" element={<OnlineOrdersPage />} />
            <Route path="online-store" element={<OnlineStorePage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="etims" element={<EtimsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="outlet-performance" element={<OutletPerformancePage />} />
            <Route path="setup-wizard" element={<SetupWizardPage />} />
            <Route path="subscription" element={<TenantSubscriptionPage />} />
            <Route path="fiscal-reports" element={<FiscalReportsPage />} />
            <Route path="eod-reports" element={<EODReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="tables" element={<TablesPage />} />
            <Route path="kitchen" element={<KitchenPage />} />
            <Route path="taxes" element={<TaxesPage />} />
            <Route path="discounts" element={<DiscountsPage />} />
            <Route path="shifts" element={<ShiftsPage />} />
            <Route path="stock-takes" element={<StockTakesPage />} />
            <Route path="stock-valuation" element={<StockValuationPage />} />
            <Route path="accounting-periods" element={<AccountingPeriodsPage />} />
            <Route path="cost-centres" element={<CostCentresPage />} />
            <Route path="store-issues" element={<StoreIssuesPage />} />
            <Route path="item-reductions" element={<ItemReductionsPage />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="qr-menu" element={<QRMenuPage />} />
          </Route>

          {/* Printer Test Page - accessible without full admin login for testing on POS devices */}
          <Route path="/test-printer" element={<PrinterTestPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </IdleTimeoutWrapper>
      </BrowserRouter>
      
      {/* PWA Components */}
      <OfflineBanner />
      <UpdateBanner />
      
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
